import React, { FunctionComponent, useState, useRef } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import _ from 'lodash'

import styles from './styles.module.css'
import { Website } from '../../../../../typings/website'
import { useEventListener } from '../../../../../utils/customHooks'

interface WebsiteSelectorProps {
  activeWebsite: Website
  websites: Array<Website>
  updateActiveWebsite(website: Website): void
}

const WebsiteSelector: FunctionComponent<WebsiteSelectorProps> = ({
  activeWebsite,
  websites,
  updateActiveWebsite,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const containerRef = useRef<HTMLDivElement>()

  const handleGlobalClick = e => {
    const container: HTMLDivElement = containerRef.current
    const target: HTMLElement = e.target

    if (isOpen && container !== target && !container.contains(target)) {
      setIsOpen(false)
    }
  }

  useEventListener('click', document, handleGlobalClick)

  const handleClick = () => {
    setIsOpen(!isOpen)
  }

  const filteredWebsites = websites
    .filter(website => website.id !== activeWebsite.id)
    .map(website => (
      <li className={styles.item} key={_.uniqueId('websites-')}>
        <button
          role="menuitem"
          onClick={() => updateActiveWebsite(website)}
          data-rv="website-selector-dropdown-item"
        >
          {website.url}
        </button>
      </li>
    ))

  filteredWebsites.push(
    <li className={styles.item} key={_.uniqueId('websites-')}>
      <Link
        role="menuitem"
        to="/app/websites"
        className={styles.manage}
        data-rv="website-selector-manage-websites"
      >
        View Websites
      </Link>
    </li>
  )

  return (
    <div
      className={styles.container}
      ref={containerRef}
      data-rv="website-selector"
    >
      {filteredWebsites.length > 0 ? (
        <button
          onClick={handleClick}
          aria-haspopup={true}
          aria-expanded={isOpen}
          aria-controls="website-selector"
          data-rv="website-selector-button"
        >
          <span className={styles.icon}>
            <FontAwesomeIcon icon={['fas', 'globe-americas']} />
          </span>
          <span
            className={styles.website}
            data-rv="website-selector-active-website"
          >
            {activeWebsite.url}
          </span>
          <span className={styles.caret}>
            <FontAwesomeIcon icon={['fas', 'sort-down']} />
          </span>
        </button>
      ) : (
        <div className={styles.tile}>
          <span className={styles.icon}>
            <FontAwesomeIcon icon={['fas', 'globe-americas']} />
          </span>
          <span
            className={styles.website}
            data-rv="website-selector-active-website"
          >
            {activeWebsite.url}
          </span>
        </div>
      )}

      {isOpen && (
        <ul
          id="website-selector"
          role="menu"
          className={styles.dropdown}
          data-rv="website-selector-dropdown"
        >
          {filteredWebsites}
        </ul>
      )}
    </div>
  )
}

export default WebsiteSelector
